import { useState, useEffect, useRef } from 'react';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useWedding } from '../../context/WeddingContext';
import { WhatsAppFab } from '../WhatsAppFab';
import { WelcomeTour } from '../WelcomeTour';

export function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const { wedding, onboardingDone, weddingLoading, saveWedding } = useWedding();
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef(null);
  const lastLoginLoggedRef = useRef(false);

  // Sinal de "conta ativa" pra futuros gatilhos de reengajamento — sem isso
  // não existia nenhum registro de quando o casal volta a acessar o painel.
  // Uma escrita por sessão (não por render) é suficiente.
  useEffect(() => {
    if (weddingLoading || !onboardingDone || lastLoginLoggedRef.current) return;
    lastLoginLoggedRef.current = true;
    saveWedding({ lastLoginAt: new Date() }).catch(() => {});
  }, [weddingLoading, onboardingDone, saveWedding]);

  // Mostra o tour de boas-vindas uma única vez, assim que a pessoa chega em
  // qualquer página do dashboard após o onboarding — não só na home, já que
  // o onboarding manda direto pra /dashboard/cards.
  useEffect(() => {
    if (!wedding) return;
    if (wedding.onboardingCompleted && !wedding.tutorialSeen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTourOpen(true);
    }
  }, [wedding?.onboardingCompleted, wedding?.tutorialSeen, wedding]);

  const handleFinishTour = async () => {
    setTourOpen(false);
    try { await saveWedding({ tutorialSeen: true }); } catch { /* swallow */ }
  };

  const slug = wedding?.slug || 'seu-casamento';
  const publicLink = `${window.location.origin}/${slug}`;

  // O scroll do dashboard acontece dentro do <main> (overflow-y-auto), não
  // na window — por isso o reset de scroll global do RouteTracker (App.jsx)
  // não tem efeito aqui. Reseta o container certo ao trocar de página.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);

  // Redirect to onboarding if setup not complete. Pequena tolerância evita
  // redirects indevidos quando o WeddingContext está hidratando entre snapshots
  // (Firestore às vezes entrega cache stale antes do dado real).
  useEffect(() => {
    if (weddingLoading) return;
    if (onboardingDone) return;
    const t = setTimeout(() => {
      if (!onboardingDone) navigate('/onboarding', { replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [weddingLoading, onboardingDone, navigate]);

  // Show nothing while checking onboarding status
  if (weddingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-rose-50">
        <div className="flex flex-col items-center gap-3">
          <svg className="animate-spin h-8 w-8 text-rose-400" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-rose-400 text-sm font-medium">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!onboardingDone) return null;

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile overlay backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar — drawer on mobile, static on desktop */}
      <div
        className={[
          'fixed inset-y-0 left-0 z-40 flex-shrink-0',
          'lg:relative lg:z-auto lg:flex',
          'transition-transform duration-300 ease-out',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        ].join(' ')}
      >
        <Sidebar onClose={() => setMobileOpen(false)} />
      </div>

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <Topbar onMenuClick={() => setMobileOpen(true)} />
        <main ref={mainRef} className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      <WhatsAppFab />

      <WelcomeTour open={tourOpen} onClose={handleFinishTour} onFinish={handleFinishTour} publicLink={publicLink} />
    </div>
  );
}
